/** Zeitlogik und Cache-Auffrischung der Termine einer Mannschaft. */
import type { QueryClient } from '@tanstack/react-query';

/** Wie lange nach dem Ende ein Termin noch als "kommend" gilt - wie bei Spielen. */
export const KULANZ_MS = 3 * 3600_000;
/** So weit zurueck erinnert die Liste an offene Anwesenheiten. */
export const OFFEN_TAGE = 7;

interface Zeitraum {
  start_at: string;
  end_at?: string | null;
}

interface Anwesenheitstermin extends Zeitraum {
  status: string;
  kind?: 'TRAINING' | 'WORK';
  confirmed_at: string | null;
  guest_of?: string | null;
}

/** Ende des Termins; fehlt es oder ist es unlesbar, gilt der Beginn. */
export function terminEnde(termin: Zeitraum): number {
  const ende = termin.end_at ? Date.parse(termin.end_at) : Number.NaN;
  return Number.isFinite(ende) ? ende : Date.parse(termin.start_at);
}

/** Kommend: bis zum Ende des Termins plus Kulanz. */
export function istKommend(termin: Zeitraum, jetzt = Date.now()): boolean {
  return terminEnde(termin) + KULANZ_MS >= jetzt;
}

/**
 * Vergangenes Training oder vergangener Arbeitseinsatz der letzten Tage, dessen
 * Anwesenheit noch niemand bestaetigt hat. Gemeinsame Termine einer anderen
 * Mannschaft fuehrt deren Leitung - sie mahnen hier nicht.
 */
export function anwesenheitOffen(termin: Anwesenheitstermin, jetzt = Date.now()): boolean {
  if (termin.status === 'CANCELLED' || termin.confirmed_at || termin.guest_of) return false;
  if (istKommend(termin, jetzt)) return false;
  return Date.parse(termin.start_at) >= jetzt - OFFEN_TAGE * 86_400_000;
}

/** Vor dem Beginn gibt es nur Rueckmeldungen, die Anwesenheit kommt erst danach. */
export function anwesenheitLabel(startAt: string, jetzt = Date.now()): 'Rückmeldungen' | 'Anwesenheit' {
  return Date.parse(startAt) > jetzt ? 'Rückmeldungen' : 'Anwesenheit';
}

/** Alles auffrischen, was Termine, Rueckmeldungen oder Anwesenheit zeigt. */
export function invalidateSports(qc: QueryClient): Promise<unknown> {
  return Promise.all(
    ['sports-sessions', 'sports-matches', 'sports-teams', 'calendar', 'sports-summary', 'attendance']
      .map((key) => qc.invalidateQueries({ queryKey: [key] })),
  );
}

const ABSAGE_MARKE = 'Absage: ';

/**
 * Haengt den Absagegrund an die Beschreibung. Eine bestehende Beschreibung
 * bleibt stehen; der Grund steht in einem eigenen Absatz mit Marke, damit er
 * beim Aufheben der Absage wieder allein entfernt werden kann.
 */
export function absageGrundAnhaengen(beschreibung: string | null | undefined, grund: string): string {
  const bisher = absageGrundEntfernen(beschreibung).text;
  return [bisher, `${ABSAGE_MARKE}${grund.trim()}`].filter(Boolean).join('\n\n');
}

/** Entfernt den beim Absagen angehaengten Grund; `geaendert` sagt, ob es einen gab. */
export function absageGrundEntfernen(beschreibung: string | null | undefined): { text: string; geaendert: boolean } {
  const absaetze = (beschreibung ?? '').split('\n\n');
  const rest = absaetze.filter((absatz) => !absatz.startsWith(ABSAGE_MARKE));
  return { text: rest.join('\n\n'), geaendert: rest.length !== absaetze.length };
}

/** Der Absagegrund zur Anzeige. Ohne Marke (alte Absagen) ist es die ganze Beschreibung. */
export function absageGrundLesen(beschreibung: string | null | undefined): string {
  const absaetze = (beschreibung ?? '').split('\n\n');
  const gefunden = absaetze.filter((absatz) => absatz.startsWith(ABSAGE_MARKE)).map((absatz) => absatz.slice(ABSAGE_MARKE.length));
  return gefunden.length > 0 ? gefunden.join(' ') : (beschreibung ?? '');
}
